import { useMemo, useState } from 'react';
import { useFlags } from '@atlaskit/flag';
import Lozenge from '@atlaskit/lozenge';
import Select from '@atlaskit/select';
import Spinner from '@atlaskit/spinner';
import { orderBy, where } from 'firebase/firestore';
import dayjs from 'dayjs';
import PageHeader from 'shared/components/page-header/page-header';
import SharedButton from 'shared/components/buttons/button';
import SharedSegmentedControl from 'shared/components/buttons/segmented-control/segmented-control';
import SharedTable from 'shared/components/table/table';
import { ISharedTableColumn, ISharedTableRow } from 'shared/components/table/table-interface';
import { useCollection } from 'core/hooks/useCollection';
import { DeliveryRequestsApiService } from 'core/api';
import DeliveryRequestsApi, {
  IRequestDecision,
  ReviewDecision,
} from 'core/api/delivery-requests/delivery-requests-api.service';
import { showErrorFlag, showSuccessFlag } from 'core/utilities/flags-helper';
import {
  DeliveryRequestStatus,
  IDeliveryRequestDao,
} from 'core/api/types/delivery-requests.interface';
import {
  AWAITING_ACTION,
  DeliveryRequestExceptionLabels,
  StatusLabels,
  isAwaitingAction,
} from 'core/constants/delivery-request-status';
import { useAwaitingRequests } from 'core/hooks/useAwaitingRequests';
import { useMarkedRequests } from 'core/hooks/useMarkedRequests';
import { useDialog } from 'core/providers/DialogProvider';
import CommitDialog from './commit-dialog/commit-dialog';
import RequestDetailsDialog, { describePlace } from './request-details-dialog/request-details-dialog';

/**
 * Every Delivery Request that has arrived, and what became of it.
 *
 * The core system logs each request and where it went; this is that record
 * made visible — the record of requests, not of email. An email that could not
 * be parsed never became a request anybody can act on, so it is written to
 * Firestore for debugging and never reaches this screen.
 *
 * Three segments, not a row of filters. A request is in exactly one of them and
 * moves between them as it is worked: Review holds what nobody has decided
 * about, Queue holds what has been decided but not carried out, Completed holds
 * what has been. No request is in two of them, which is what makes the counts
 * mean something — an "everything" segment would have shown each waiting
 * request twice and told you nothing about how much was left to do. Marking a request moves it out of Review and into Queue — the same
 * gesture that decides its fate also takes it off the list of things to decide,
 * so the review list empties as it is worked rather than staying the same
 * length with marks scattered through it.
 *
 * It opens on Review: this is a screen people work from, and the work is the
 * requests nothing has happened to yet.
 */

type RequestRow = IDeliveryRequestDao & { [key: string]: unknown };

type RequestTab = 'review' | 'queue' | 'completed';

/**
 * What the screen opens on.
 *
 * The requests waiting for a person, not the whole record: opening on
 * everything meant the work that had stopped was mixed in with hundreds of
 * requests that had already been dealt with, and had to be found before
 * anything could be done.
 */
const DEFAULT_TAB: RequestTab = 'review';

/**
 * The statuses that mean nobody has decided anything yet.
 *
 * `unreadable` is not among them: a request that could not be parsed has
 * nothing to book and no type to forward it by, so the only thing a reviewer
 * could ever do with one was close it. Removed 17 Sep 2026 along with Close.
 */
const REVIEW_STATUSES: DeliveryRequestStatus[] = ['needsReview'];

/** The statuses that mean a request has been dealt with, whatever the outcome. */
const COMPLETED_STATUSES: DeliveryRequestStatus[] = ['booked', 'forwarded', 'declined'];

/**
 * Narrowing the record, offered only on Completed.
 *
 * These are outcomes rather than work, so they have no business in the segments
 * beside Review and Queue, which are the two lists somebody is working.
 */
const HISTORY_FILTERS: Array<{ key: 'all' | DeliveryRequestStatus; label: string }> = [
  { key: 'all', label: 'Everything' },
  { key: 'booked', label: 'Booked' },
  { key: 'forwarded', label: 'Forwarded' },
];

const STATUS_APPEARANCE: Record<DeliveryRequestStatus, 'success' | 'inprogress' | 'removed' | 'moved' | 'default'> = {
  booked: 'success',
  forwarded: 'inprogress',
  needsReview: 'moved',
  unreadable: 'removed',
  declined: 'default',
  received: 'default',
};

const COLUMNS: ISharedTableColumn[] = [
  { key: 'arrived', label: 'Arrived', templateId: 'arrived', width: 14 },
  { key: 'request', label: 'Request', templateId: 'request', width: 32 },
  { key: 'route', label: 'Collection and destination', templateId: 'route', width: 26 },
  { key: 'status', label: 'What happened', templateId: 'status', width: 28 },
];

/** The marks a request can carry, used to select the queue. */
export const MARK_DECISIONS: ReviewDecision[] = ['accept', 'decline'];

/** The buckets, in the order they are counted on the Queue. */
const DECISION_ORDER: ReviewDecision[] = ['accept', 'decline'];

/**
 * What a row's control can be set to, decision or not.
 *
 * `pending` is not a decision and is never stored — it is the absence of one,
 * given a name and a segment so that "nobody has decided about this yet" is a
 * position on the same control rather than the state you are in when no button
 * is pressed. Choosing it is how a request goes back to Review.
 */
type RowMark = 'pending' | ReviewDecision;

const ROW_MARKS: RowMark[] = ['pending', 'accept', 'decline'];

const ROW_MARK_LABELS: Record<RowMark, string> = {
  pending: 'Pending',
  accept: 'Accept',
  decline: 'Decline',
};

/** One word each, for the counts. The full wording is on the rows themselves. */
const DECISION_SHORT_LABELS: Record<ReviewDecision, string> = {
  accept: 'Accept',
  decline: 'Decline',
};

const DECISION_LABELS: Record<ReviewDecision, string> = {
  accept: 'Accept and book',
  decline: 'Decline and forward',
};

const DeliveryRequestsList = () => {
  const flags = useFlags();
  const dialog = useDialog();
  const [tab, setTab] = useState<RequestTab>(DEFAULT_TAB);
  const [historyStatus, setHistoryStatus] = useState<'all' | DeliveryRequestStatus>('all');
  // Which rows are mid-call, so a second click cannot race the first.
  const [marking, setMarking] = useState<Record<string, boolean>>({});

  // Newest first: the thing that just arrived is the thing being asked about.
  //
  // The filter is a query constraint rather than something applied to the
  // rows: the screen loads a page at a time, so filtering what happens to be
  // loaded would show an empty table while plenty matched further down.
  const constraints = useMemo(() => {
    // Not yet decided about. `marked` is a field rather than a test for the
    // absence of one because Firestore cannot ask for a document missing a
    // field, and this list has to be right past the first page.
    if (tab === 'review') {
      return [
        where('marked', '==', false),
        where('status', 'in', REVIEW_STATUSES),
        orderBy('created.at', 'desc'),
      ];
    }

    // Ordered by arrival, like every other view, and never by when the mark
    // was made: re-marking rewrites that timestamp, so changing your mind
    // about one request would reshuffle the list you were reading.
    if (tab === 'queue') {
      return [where('pendingDecision.decision', 'in', MARK_DECISIONS), orderBy('created.at', 'desc')];
    }

    // Settled requests only. Everything here has been dealt with, so nothing on
    // this segment is also sitting in Review or Queue.
    if (historyStatus === 'all') {
      return [where('status', 'in', COMPLETED_STATUSES), orderBy('created.at', 'desc')];
    }
    return [where('status', '==', historyStatus), orderBy('created.at', 'desc')];
  }, [tab, historyStatus]);

  const {
    data: requests,
    loading,
    loadMore,
    loadingMore,
    hasMore,
  } = useCollection<IDeliveryRequestDao>({
    apiService: DeliveryRequestsApiService,
    constraints,
  });

  // Asked of the database, and shared with the sidebar badge, so the two
  // cannot disagree and neither is capped at whatever is on screen.
  const awaiting = useAwaitingRequests();

  const visible = requests ?? [];

  // The whole holding area, not the marks visible on this page: the bar says
  // what committing would do, and it commits everything that is marked.
  const { requests: marked, label: markedLabel } = useMarkedRequests();

  /**
   * How many are marked for each outcome.
   *
   * Every bucket is shown, including the empty ones: a fixed row of three is
   * read at a glance, where a list that grows and shrinks has to be read
   * properly to see what is missing from it. This is what committing would do,
   * said before the button rather than in a banner nobody reads twice.
   */
  const buckets = useMemo(
    () =>
      DECISION_ORDER.map((decision) => ({
        decision,
        count: marked.filter((request) => request.pendingDecision?.decision === decision).length,
      })),
    [marked],
  );

  /**
   * How many of the marked set would actually become jobs.
   *
   * Only accepts can go into a run: a decline forwards the request to somebody
   * else, so it produces no work for a driver. Offering to build a run of
   * nothing would be a button that always failed.
   */
  const accepting = useMemo(
    () => marked.filter((request) => request.pendingDecision?.decision === 'accept').length,
    [marked],
  );

  /**
   * Says where a request just went.
   *
   * Deciding about one takes it off the list being worked, so the row vanishes
   * from under the cursor. Without a word that reads as a click that did
   * nothing, or as a request that has been lost — and the second reading is the
   * one this whole feature exists to prevent.
   */
  const announceMark = (decision: ReviewDecision) =>
    showSuccessFlag(
      'Moved to the queue',
      `Marked to ${DECISION_LABELS[decision].toLowerCase()}. Nothing happens until you commit.`,
      flags,
    );

  const announceReturn = () =>
    showSuccessFlag('Back in Review', 'The mark is off, and nothing was carried out.', flags);

  /**
   * Marks a request, or takes the mark off if it already says this.
   *
   * A round trip rather than local state: the mark is stored on the request so
   * it survives navigating away and whoever else is working the queue sees it.
   * The live subscription brings the change back, so nothing is set here.
   */
  /**
   * Holds a row busy for the length of a call: its control is disabled and a
   * spinner shows beside it, so a second click cannot race the first. Every
   * choice on the control goes through here — Pending included, which used to
   * skip it and so gave no sign it was doing anything.
   */
  const whileMarking = async (uid: string, action: () => Promise<void>) => {
    if (marking[uid]) return;
    setMarking((current) => ({ ...current, [uid]: true }));
    try {
      await action();
    } finally {
      setMarking((current) => {
        const { [uid]: _done, ...rest } = current;
        return rest;
      });
    }
  };

  const mark = (request: IDeliveryRequestDao, decision: ReviewDecision) =>
    whileMarking(request.uid, async () => {
      const uid = request.uid;
      try {
        // Marking the same thing twice takes the mark off, so a mis-click is
        // undone the same way it was made.
        if (request.pendingDecision?.decision === decision) {
          await DeliveryRequestsApi.clearMark(uid);
          announceReturn();
        } else {
          await DeliveryRequestsApi.mark(uid, decision);
          announceMark(decision);
        }
      } catch (caught) {
        showErrorFlag('That could not be marked', (caught as Error).message, flags);
      }
    });

  /** Takes one request out of the queue, leaving the rest of it alone. */
  const removeFromQueue = (uid: string) =>
    whileMarking(uid, async () => {
      try {
        await DeliveryRequestsApi.clearMark(uid);
        announceReturn();
      } catch (caught) {
        showErrorFlag('That could not be taken out', (caught as Error).message, flags);
      }
    });

  /**
   * What the queue held when the button was pressed.
   *
   * Taken once rather than read again when the call goes out: the queue is a
   * live subscription and shared, so anything somebody else marked while this
   * was being decided would otherwise be carried out by it — work nobody on
   * this screen had seen, let alone agreed to.
   */
  const queuedNow = (): IRequestDecision[] =>
    marked
      .filter((request) => request.pendingDecision)
      .map((request) => ({
        uid: request.uid,
        decision: request.pendingDecision!.decision,
        ...(request.pendingDecision!.reason ? { reason: request.pendingDecision!.reason } : {}),
      }));

  /**
   * Asks what should happen to what is in the queue.
   *
   * The decisions go in as a snapshot, so the dialog carries out what was on
   * screen when it opened rather than whatever the queue has become by the time
   * somebody has looked at a route and chosen a driver.
   */
  const openCommit = () => {
    const decisions = queuedNow();
    if (!decisions.length) {
      showErrorFlag('Nothing to commit', 'The queue is empty.', flags);
      return;
    }

    dialog?.openDialog(<CommitDialog decisions={decisions} accepting={accepting} />);
  };

  const rows: ISharedTableRow<RequestRow>[] = visible.map((request) => ({
    key: request.uid,
    data: request as RequestRow,
  }));

  return (
    <>
      <PageHeader
        title='Delivery requests'
        subtitle='Every request that has arrived, and what became of it.'
      />

      <div className='mt-6 overflow-y-auto pb-10'>
        <div className='mb-4 flex flex-wrap items-center justify-between gap-3'>
          <div className='flex flex-wrap items-center gap-3'>
            <SharedSegmentedControl<RequestTab>
              ariaLabel='Which delivery requests to show'
              testIdPrefix='requests-tab'
              value={tab}
              onChange={setTab}
              options={[
                // Amber while anything is waiting: this is the count a banner
                // above the table used to shout about, in the place you look
                // before choosing where to go.
                {
                  key: 'review',
                  label: 'Review',
                  count: awaiting.count,
                  // "99+" past the subscription's cap, which says the same
                  // thing a larger exact number would.
                  countLabel: awaiting.label,
                  emphasis: 'attention',
                },
                // Capped the same way, and for the same reason: a queue that
                // has run past its subscription should say so rather than sit
                // at a number that stops moving.
                { key: 'queue', label: 'Queue', count: marked.length, countLabel: markedLabel },
                { key: 'completed', label: 'Completed' },
              ]}
            />

            {tab === 'completed' && (
              <div className='w-56' data-testid='history-filter'>
                <Select
                  inputId='history-filter'
                  isSearchable={false}
                  spacing='compact'
                  aria-label='Narrow the record to one outcome'
                  options={HISTORY_FILTERS}
                  getOptionLabel={(option) => option.label}
                  getOptionValue={(option) => option.key}
                  value={HISTORY_FILTERS.find((option) => option.key === historyStatus)}
                  onChange={(option) => setHistoryStatus(option?.key ?? 'all')}
                />
              </div>
            )}
          </div>

          {/* Beside the segment rather than in a bar over the table: the rows on
              screen are exactly what these carry out, and the counts to their
              left already say what that means. Over Review they acted on
              requests that were not in front of you, which is the wrong place
              to learn that six things are about to be booked. */}
          {tab === 'queue' && marked.length > 0 && (
            <div className='flex gap-2' data-testid='queue-actions'>
              <SharedButton
                type='button'
                appearance='primary'
                label={`Commit ${marked.length}`}
                testId='commit-decisions'
                onClick={openCommit}
              />
            </div>
          )}
        </div>

        {/* What committing would do, counted per outcome. Same shape as the
            counters over Deliveries, which is where anybody who uses both
            screens has already learnt to read a number. */}
        {tab === 'queue' && (
          <div className='mb-4 flex border-b py-4 body-02' data-testid='queue-buckets'>
            {buckets.map(({ decision, count }, index) => (
              <p
                key={decision}
                data-testid={`queue-bucket-${decision}`}
                className={index === 0 ? undefined : 'ml-2 border-l pl-2'}
              >
                {DECISION_SHORT_LABELS[decision]}: <span className='font-semibold'>{count}</span>
              </p>
            ))}
          </div>
        )}

        {/* Shown on every re-query, not just the first. The segment is a query
            constraint, so keeping the previous rows on screen while the new one
            runs shows rows that do not match what was just chosen. */}
        {loading ? (
          <div data-testid='requests-loading' className='rounded-md bg-white p-8 text-center shadow-md'>
            <p className='body-01 text-gray-600'>Loading…</p>
          </div>
        ) : (
          <div data-testid='requests-table'>
            <SharedTable<RequestRow>
              columns={COLUMNS}
              rows={rows}
              customTemplates={[
                {
                  id: 'arrived',
                  template: (row: RequestRow) => (
                    <div>
                      <p>{row.created?.at ? dayjs(row.created.at.toDate()).format('DD/MM/YYYY') : 'Unknown'}</p>
                      <p className='mt-1 text-gray-500'>
                        {row.created?.at ? dayjs(row.created.at.toDate()).format('HH:mm') : ''}
                      </p>
                    </div>
                  ),
                },
                {
                  id: 'request',
                  template: (row: RequestRow) => (
                    <div>
                      <p>{row.request?.requestType ?? 'Could not be read'}</p>
                      <p className='mt-1 text-gray-500'>
                        {row.request ? `${row.request.requesterName} · ${row.request.department}` : row.source.from}
                      </p>
                      {/* The number the requester has in their own inbox, so a
                          phone call about "booking 975" can be matched to a row
                          without searching the raw email. */}
                      {(row.reference ?? row.source.reference) && (
                        <p className='mt-1 text-gray-500' data-testid='request-reference'>
                          Ref. {row.reference ?? row.source.reference}
                        </p>
                      )}
                      {row.request?.urgent && (
                        <span className='mt-1 inline-block'>
                          <Lozenge appearance='removed'>Urgent</Lozenge>
                        </span>
                      )}
                      {/* The row is a summary; the reason, equipment and any
                          note on the form are only here. */}
                      {row.request && (
                        <div className='mt-2'>
                          <SharedButton
                            type='button'
                            appearance='default'
                            spacing='compact'
                            label='View details'
                            testId='view-request-details'
                            onClick={() => dialog?.openDialog(<RequestDetailsDialog request={row} />)}
                          />
                        </div>
                      )}
                    </div>
                  ),
                },
                {
                  id: 'route',
                  template: (row: RequestRow) =>
                    row.request ? (
                      <div>
                        {/* Site and where within it. For "Other Location" the
                            site alone says nothing — the address is in the
                            location and postcode. */}
                        <p data-testid='request-collection'>
                          {describePlace(
                            row.request.collectionSite,
                            row.request.collectionLocation,
                            row.request.collectionPostcode,
                          )}
                        </p>
                        <p className='text-gray-500' data-testid='request-destination'>
                          to{' '}
                          {describePlace(
                            row.request.destinationSite,
                            row.request.destinationLocation,
                            row.request.destinationPostcode,
                          )}
                        </p>
                        <p className='mt-1 text-gray-500'>
                          {row.request.requiredDate} {row.request.requiredTime}
                        </p>
                      </div>
                    ) : (
                      <p className='text-gray-500'>Not known</p>
                    ),
                },
                {
                  id: 'status',
                  template: (row: RequestRow) => (
                    <div>
                      <span data-testid='request-status'>
                        <Lozenge appearance={STATUS_APPEARANCE[row.status] ?? 'default'}>
                          {StatusLabels[row.status] ?? row.status}
                        </Lozenge>
                      </span>

                      {/* Why, not just what. A reviewer cannot tell a decision
                          from a fallback without it. */}
                      {row.recommendation?.reason && (
                        <p data-testid='request-reason' className='mt-1 text-gray-500'>
                          {row.recommendation.reason}
                        </p>
                      )}

                      {row.handling?.forwardedTo && (
                        <p className='mt-1 text-gray-500'>Sent to {row.handling.forwardedTo.email}</p>
                      )}

                      {/* Written out rather than left as codes: these are the
                          reasons a person has to act on. */}
                      {row.exceptions?.map((exception) => (
                        <p key={exception} className='mt-1 text-amber-700'>
                          {DeliveryRequestExceptionLabels[exception] ?? exception}
                        </p>
                      ))}

                      {row.actionError && <p className='mt-1 text-red-700'>{row.actionError}</p>}
                      {/* Amber, not red, and worded as what the requester is
                          missing rather than as a failure: the job is arranged,
                          and this only means nobody told them. */}
                      {row.requesterMailError && (
                        <p className='mt-1 text-amber-700' data-testid='requester-mail-error'>
                          The requester was not sent a confirmation: {row.requesterMailError}
                        </p>
                      )}

                      {isAwaitingAction(row) && (
                        <div className='mt-2'>
                          {row.pendingDecision ? (
                            // Named, because the queue is shared: finding a
                            // request already marked should say who by rather
                            // than look like something you did and forgot.
                            <p data-testid='marked-as' className='text-blue-800'>
                              Marked to {DECISION_LABELS[row.pendingDecision.decision].toLowerCase()} by{' '}
                              {row.pendingDecision.by?.fullName ?? 'someone'}
                            </p>
                          ) : null}
                          <div className='mt-1 flex flex-wrap items-center gap-2'>
                            <SharedSegmentedControl<RowMark>
                              size='compact'
                              ariaLabel='What should happen to this request'
                              testIdPrefix='mark'
                              // Never undefined: a request with no decision on
                              // it is Pending, which is a position on the
                              // control rather than the absence of one.
                              value={row.pendingDecision?.decision ?? 'pending'}
                              disabled={marking[row.uid]}
                              onChange={(choice) => {
                                if (choice === 'pending') return removeFromQueue(row.uid);
                                // Choosing what it already says is somebody
                                // confirming, not somebody undoing: Pending is
                                // how a mark comes off.
                                if (row.pendingDecision?.decision === choice) return undefined;
                                return mark(row, choice);
                              }}
                              options={ROW_MARKS.map((choice) => ({
                                key: choice,
                                label: ROW_MARK_LABELS[choice],
                              }))}
                            />
                            {marking[row.uid] && (
                              <span data-testid='mark-saving' className='inline-flex'>
                                <Spinner size='small' label='Saving' />
                              </span>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  ),
                },
              ]}
            />

            {/* A page at a time, so a long history never has to be loaded to
                look at the recent one. Absent rather than disabled when there
                is no more, so the end of the list is unambiguous. */}
            {hasMore && (
              <div className='mt-4 flex justify-center'>
                <SharedButton
                  type='button'
                  appearance='default'
                  label='Load more'
                  testId='load-more-requests'
                  isLoading={loadingMore}
                  onClick={loadMore}
                />
              </div>
            )}
          </div>
        )}
      </div>
    </>
  );
};

export default DeliveryRequestsList;
export { AWAITING_ACTION };
